<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";
import { PosStation } from "../../../../types/models";
import DateInput from "../../../../components/ui/form/DateInput.vue";
import TextAreaInput from "../../../../components/ui/form/TextAreaInput.vue";
import FileUpload from "../../../../components/ui/form/FileUpload.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  ignorePermission?: boolean;
  listCreateName?: string;
  minimized?: boolean;
  posStation?: PosStation | undefined;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  id: props.data?.id || "",
  customers_id: props.data?.customers_id || "",
  name: props.data?.name || "",
  project_accounts_id: props.data?.project_accounts_id || "",
  start_date: props.data?.start_date || "",
  end_date: props.data?.end_date || "",
  prefix: props.data?.prefix || "",
  suffix: props.data?.suffix || "",
  ref_no: props.data?.ref_no || "",
  project_value: props.data?.project_value || 0,
  customer_reference: props.data?.customer_reference || "",
  remarks: props.data?.remarks || "",
  attachment: props.data?.attachment || "",
  uid: props.data?.uid || "",
  document_no: props.data?.document_no || "",
});

setFieldRules("name", [required("Name is required")]);
setFieldRules("customers_id", [required("Customer is required")]);
setFieldRules("project_accounts_id", [
  required("Project Account is required"),
]);

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        setFormData(response.data);
      }
    }
  }
};

const handleGetRecord = async () => {
  if (!props.data) return;
  const params = {
    id: props.data?.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    console.log("setFormData", response.data);
    setFormData(response.data);
  }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == "view";
  min.value = props.minimized || false;
  if (props.mode == "create") {
    resetForm();
  } else if (props.data) {
    handleGetRecord();
  }
});

const min = ref(false);
</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :ignorePermission="ignorePermission"
    :tabList="[]"
    :formData="formData"
    :showSaveNew="!min"
    :showSave="!min"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
        <div class="grid md:grid-cols-2 gap-6">
          <TextInput
            v-model="fields.name.value"
            label="Project Name"
            :readonly="isReadOnly"
            :error="fields.name.error"
          />
          <TextInput
            v-model="fields.document_no.value"
            label="Project No"
            :readonly="true"
            :error="fields.document_no.error"
          />
          <SearchSelect
            v-model="fields.customers_id.value"
            label="Customer"
            end-point="revenue/master-data/customers"
            :readonly="isReadOnly"
            :error="fields.customers_id.error"
            :allowNewRecord="true"
          />          

          <TextInput
            v-model="fields.customer_reference.value"
            label="Customer Reference"
            :readonly="isReadOnly && fields.customer_reference.value == true"
            :min="0"
            :error="
              fields.customer_reference.touched
                ? fields.customer_reference.error
                : null
            "
            @blur="fields.customer_reference.touched = true"
          />
          
          <div class="grid md:grid-cols-2 gap-6">
          <DateInput
            v-model="fields.start_date.value"
            label="Start Date"
            :readonly="isReadOnly"
            :error="
              fields.start_date.touched
                ? fields.start_date.error
                : null
            "
            @blur="fields.start_date.touched = true"
          />

          <DateInput
            v-model="fields.end_date.value"
            label="End Date"
            :readonly="isReadOnly"
            :error="
              fields.end_date.touched
                ? fields.end_date.error
                : null
            "
            @blur="fields.end_date.touched = true"
          />

          </div>

          <SearchSelect
            v-model="fields.project_accounts_id.value"
            label="Project Account"
            end-point="finance/accounting/chart-of-accounts"
            :readonly="isReadOnly"
            :error="
              fields.project_accounts_id.touched
                ? fields.project_accounts_id.error
                : null
            "
            @blur="fields.project_accounts_id.touched = true"
          />
          
          <NumberInput
            v-model="fields.project_value.value"
            label="Project Value"
            :readonly="isReadOnly && fields.project_value.value == true"
            :min="0"
            :error="
              fields.project_value.touched
                ? fields.project_value.error
                : null
            "
            @blur="fields.project_value.touched = true"
          />

          <TextAreaInput
            v-model="fields.remarks.value"
            label="Remarks"
            :readonly="isReadOnly && fields.remarks.value == true"
            :min="0"
            :error="
              fields.remarks.touched
                ? fields.remarks.error
                : null
            "
            @blur="fields.remarks.touched = true"
          />

          <FileUpload 
            v-model="fields.attachment.value"
            label="Attachment"
            path="projects"
            :readonly="isReadOnly && fields.attachment.value == true"
            :error="
              fields.attachment.touched
                ? fields.attachment.error
                : null
            "
            @blur="fields.attachment.touched = true" />

        </div>
    </template>
  </ModalFormTemplate>
</template>
